/**
 * Platform pinning — where a member is allowed to be, and where to send them
 * when they are somewhere else. Pure so PlatformGuard's rules are testable.
 *
 * Two layers, checked in order:
 *
 *   1. Workspace kind. A standalone product workspace (sales / clients /
 *      sow_desk) has no owner dashboard: EVERY member, owner included, is
 *      pinned to the workspace's home product. /v1/me sends homePath; a kind
 *      map covers an API that predates it.
 *   2. Assigned platform. In a business workspace a member/viewer whose
 *      department sets a platform is pinned to that platform's tree.
 *      Owners/admins roam freely. A member assigned to an add-on the
 *      workspace does not have (tax / sow off) is left alone — the add-on
 *      gate would bounce them back and loop.
 *
 * Only the protected app trees are governed; public, /preview and auth
 * routes are untouched.
 */

export interface PinInput {
  userId: string;
  role: string;
  workspaceKind: string;
  homePath?: string | null;
  assignedPlatform: string | null;
  features?: { tax?: boolean; sow?: boolean } | null;
}

/** Home of each standalone workspace kind (fallback when me.homePath is absent). */
const KIND_HOME: Record<string, string> = {
  sales: '/sales',
  clients: '/clients',
  sow_desk: '/sow',
};

const PLATFORM_HOME: Record<string, string> = {
  sales: '/sales',
  tax: '/tax',
  sow: '/sow',
  clients: '/clients',
  finance: '/dashboard',
  operations: '/dashboard',
  marketing: '/dashboard',
  executive: '/dashboard',
};

const PLATFORM_ALLOWED_PREFIX: Record<string, string[]> = {
  sales: ['/sales'],
  tax: ['/tax'],
  sow: ['/sow'],
  clients: ['/clients'],
  finance: ['/dashboard'],
  operations: ['/dashboard'],
  marketing: ['/dashboard'],
  executive: ['/dashboard'],
};

/** The protected app route trees pinning governs. */
export const APP_PREFIXES = ['/dashboard', '/sales', '/tax', '/clients', '/sow'];

export function underAny(path: string, prefixes: readonly string[]): boolean {
  return prefixes.some(pre => path === pre || path.startsWith(pre + '/'));
}

/** Home tree of a standalone workspace, or null for the business workspace. */
export function standaloneHome(me: Pick<PinInput, 'workspaceKind' | 'homePath'>): string | null {
  if (me.workspaceKind === 'business') return null;
  return me.homePath || KIND_HOME[me.workspaceKind] || null;
}

/**
 * Where to redirect for this path, or null to leave the user where they are.
 */
export function resolvePin(me: PinInput, pathname: string): string | null {
  if (!me.userId) return null;
  if (!underAny(pathname, APP_PREFIXES)) return null; // public / preview / auth

  const home = standaloneHome(me);
  if (home) return underAny(pathname, [home]) ? null : home;

  const ap = me.assignedPlatform;
  if (!ap) return null;
  if (me.role === 'owner' || me.role === 'admin') return null; // unrestricted
  if (ap === 'tax' && me.features?.tax !== true) return null;
  if (ap === 'sow' && me.features?.sow !== true) return null;

  const allowed = underAny(pathname, PLATFORM_ALLOWED_PREFIX[ap] ?? []);
  const target = PLATFORM_HOME[ap];
  return !allowed && target ? target : null;
}
